import { keepPreviousData, queryOptions, skipToken } from '@tanstack/react-query'
import {
  fetchAttendanceHistory,
  fetchAvailableCollaborators,
  fetchManualAttendanceCandidates,
  fetchCollaborator,
  fetchCollaboratorContracts,
  fetchHrAnalytics,
  fetchInterimAgencies,
  fetchHrDashboardAvailableStores,
  fetchHrDashboardCoverage,
  fetchHrDashboardOverview,
  fetchHrDashboardPlanningStatus,
  fetchHrDashboardTrends,
  fetchHrEffectifDetail,
  fetchHrFraisDetail,
  fetchHrRemoteStorePeriodMetrics,
  fetchHrSalesPeaks,
  fetchHrTheftPeaks,
  fetchHrStoreWeeklyMetrics,
  fetchHrSuiviParBoutique,
  fetchHrUnavailabilityCalendar,
  fetchLastOptimization,
  fetchPlanningConfig,
  fetchTransportSegments,
  fetchWorkShifts,
  type FetchAttendanceHistoryParams,
  type FetchAvailableCollaboratorsParams,
  type FetchManualAttendanceCandidatesParams,
  type FetchHrAnalyticsParams,
  type FetchHrDashboardParams,
  type FetchHrDashboardPlanningStatusParams,
  type FetchHrDashboardTrendsParams,
  type FetchHrRemoteStorePeriodMetricsParams,
  type FetchHrSalesPeaksParams,
  type FetchHrTheftPeaksParams,
  type FetchHrStoreWeeklyMetricsParams,
  type FetchHrUnavailabilityCalendarParams,
  type FetchLastOptimizationParams,
  type FetchTransportSegmentsParams,
  type FetchWorkShiftsParams,
} from './hr.queries'
import { normalizeHrDashboardParams } from './hr-dashboard-query-params'
import { keys } from './keys'
import {
  fetchSmsTrackingList,
  fetchSmsTrackingStats,
  type FetchSmsTrackingListParams,
  type FetchSmsTrackingStatsParams,
} from './sms-tracking.queries'

export const getCollaboratorOptions = (id: number | undefined) =>
  queryOptions({
    queryKey: keys.hr.collaborators.byId(id),
    queryFn: id !== undefined ? () => fetchCollaborator(id) : skipToken,
  })

// Collaborator contracts (history) query options
export const getCollaboratorContractsOptions = (collaboratorId: number | undefined) =>
  queryOptions({
    queryKey: keys.hr.collaborators.contracts(collaboratorId),
    queryFn:
      collaboratorId !== undefined ? () => fetchCollaboratorContracts(collaboratorId) : skipToken,
  })

// Interim agencies query options
export const getInterimAgenciesOptions = (includeArchived?: boolean) =>
  queryOptions({
    queryKey: keys.hr.interimAgencies.list(includeArchived),
    queryFn: () => fetchInterimAgencies(includeArchived),
  })

// Work shifts query options
export const getWorkShiftsOptions = (params?: FetchWorkShiftsParams) =>
  queryOptions({
    queryKey: keys.hr.workShifts.list(params),
    queryFn: () => fetchWorkShifts(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2, // 2 minutes — pointage édité par plusieurs mains, on garde + de fraîcheur
  })

// Transport segments (déplacement payé) query options
export const getTransportSegmentsOptions = (params: FetchTransportSegmentsParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.transportSegments(params),
    queryFn: params ? () => fetchTransportSegments(params) : skipToken,
    placeholderData: keepPreviousData,
  })

// Planning config query options
export const getPlanningConfigOptions = () =>
  queryOptions({
    queryKey: keys.hr.planningConfig.all(),
    queryFn: () => fetchPlanningConfig(),
  })

// Available collaborators query options
export const getAvailableCollaboratorsOptions = (
  params: FetchAvailableCollaboratorsParams | undefined,
) =>
  queryOptions({
    queryKey: keys.hr.collaborators.available(params),
    queryFn: params ? () => fetchAvailableCollaborators(params) : skipToken,
  })

export const getManualAttendanceCandidatesOptions = (
  params: FetchManualAttendanceCandidatesParams | undefined,
) =>
  queryOptions({
    queryKey: keys.hr.collaborators.manualAttendanceCandidates(params),
    queryFn: params ? () => fetchManualAttendanceCandidates(params) : skipToken,
  })

export const getAttendanceHistoryOptions = (params: FetchAttendanceHistoryParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.attendanceHistory.list(params),
    queryFn: params ? () => fetchAttendanceHistory(params) : skipToken,
  })

export const getHrAnalyticsOptions = (params?: FetchHrAnalyticsParams) =>
  queryOptions({
    queryKey: keys.hr.analytics(params),
    queryFn: () => fetchHrAnalytics(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

export const getHrSalesPeaksOptions = (params?: FetchHrSalesPeaksParams) =>
  queryOptions({
    queryKey: keys.hr.salesPeaks(params),
    queryFn: () => fetchHrSalesPeaks(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

export const getHrTheftPeaksOptions = (params?: FetchHrTheftPeaksParams) =>
  queryOptions({
    queryKey: keys.hr.theftPeaks(params),
    queryFn: () => fetchHrTheftPeaks(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

export const getHrStoreWeeklyMetricsOptions = (
  params: FetchHrStoreWeeklyMetricsParams | undefined,
) =>
  queryOptions({
    queryKey: keys.hr.storeWeeklyMetrics(params),
    queryFn: params ? () => fetchHrStoreWeeklyMetrics(params) : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

export const getHrRemoteStorePeriodMetricsOptions = (
  params: FetchHrRemoteStorePeriodMetricsParams | undefined,
) =>
  queryOptions({
    queryKey: keys.hr.remoteStorePeriodMetrics(params),
    queryFn: params ? () => fetchHrRemoteStorePeriodMetrics(params) : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

export const getHrDashboardOverviewOptions = (params: FetchHrDashboardParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.dashboard.overview(
      params === undefined ? undefined : normalizeHrDashboardParams(params),
    ),
    queryFn: params
      ? ({ signal }) => fetchHrDashboardOverview(normalizeHrDashboardParams(params), signal)
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

export const getHrDashboardTrendsOptions = (params: FetchHrDashboardTrendsParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.dashboard.trends(
      params === undefined ? undefined : normalizeHrDashboardParams(params),
    ),
    queryFn: params
      ? ({ signal }) => fetchHrDashboardTrends(normalizeHrDashboardParams(params), signal)
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

export const getHrDashboardCoverageOptions = (params: FetchHrDashboardParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.dashboard.coverage(
      params === undefined ? undefined : normalizeHrDashboardParams(params),
    ),
    queryFn: params
      ? ({ signal }) => fetchHrDashboardCoverage(normalizeHrDashboardParams(params), signal)
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

export const getHrDashboardPlanningStatusOptions = (
  params: FetchHrDashboardPlanningStatusParams | undefined,
) =>
  queryOptions({
    queryKey: keys.hr.dashboard.planningStatus(
      params === undefined ? undefined : normalizeHrDashboardParams(params),
    ),
    queryFn: params
      ? ({ signal }) => fetchHrDashboardPlanningStatus(normalizeHrDashboardParams(params), signal)
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

export const getHrEffectifDetailOptions = (params: FetchHrDashboardParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.dashboard.effectifDetail(
      params === undefined ? undefined : normalizeHrDashboardParams(params),
    ),
    queryFn: params
      ? ({ signal }) => fetchHrEffectifDetail(normalizeHrDashboardParams(params), signal)
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

export const getHrFraisDetailOptions = (params: FetchHrDashboardParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.dashboard.fraisDetail(
      params === undefined ? undefined : normalizeHrDashboardParams(params),
    ),
    queryFn: params
      ? ({ signal }) => fetchHrFraisDetail(normalizeHrDashboardParams(params), signal)
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

export const getHrSuiviParBoutiqueOptions = (params: FetchHrDashboardParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.dashboard.suiviParBoutique(
      params === undefined ? undefined : normalizeHrDashboardParams(params),
    ),
    queryFn: params
      ? ({ signal }) => fetchHrSuiviParBoutique(normalizeHrDashboardParams(params), signal)
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

export const getHrDashboardAvailableStoresOptions = () =>
  queryOptions({
    queryKey: keys.hr.dashboard.availableStores(),
    queryFn: ({ signal }) => fetchHrDashboardAvailableStores(signal),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

export const getHrUnavailabilityCalendarOptions = (
  params: FetchHrUnavailabilityCalendarParams | undefined,
) =>
  queryOptions({
    queryKey: keys.hr.absenceCalendar.list(params),
    queryFn: params ? () => fetchHrUnavailabilityCalendar(params) : skipToken,
    placeholderData: keepPreviousData,
  })

export const getLastOptimizationOptions = (params: FetchLastOptimizationParams | undefined) =>
  queryOptions({
    queryKey: keys.hr.lastOptimization(params),
    queryFn: params ? () => fetchLastOptimization(params) : skipToken,
  })

export const getSmsTrackingListOptions = (params: FetchSmsTrackingListParams) =>
  queryOptions({
    queryKey: keys.hr.smsTracking.list(params),
    queryFn: () => fetchSmsTrackingList(params),
    placeholderData: keepPreviousData,
  })

export const getSmsTrackingStatsOptions = (params: FetchSmsTrackingStatsParams) =>
  queryOptions({
    queryKey: keys.hr.smsTracking.stats(params),
    queryFn: () => fetchSmsTrackingStats(params),
    placeholderData: keepPreviousData,
  })
